import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, ClubSettings } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Cover, authorsOf } from "./BookBits";
import { MoreIcon } from "./Icons";
import type { Flyleaf } from "./Book3D";

/*
 * Suggestions (PLAN.md §5b): the "Want to read" button and the suggestion row,
 * shared by the Shelf's pane and the Suggestions page. Built from the parts
 * the app already has: the deck row, the outlined Update-style button (filled
 * once tapped), and the row's ⋯ menu.
 */

export function useSuggestionRefresh(clubId: string) {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ["books", clubId] }), qc.invalidateQueries({ queryKey: ["suggestions", clubId] })]);
}

const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** "Want to read 7", outlined; filled with a tick once it's yours. In Past it brings the book back. */
export function WantButton({ clubId, b, meId }: { clubId: string; b: ClubBook; meId?: string }) {
  const refresh = useSuggestionRefresh(clubId);
  const s = b.suggestion;
  const toggle = useMutation({ mutationFn: (wanted: boolean) => api.setInterest(clubId, b.id, wanted), onSuccess: refresh });
  const revive = useMutation({ mutationFn: () => api.revive(clubId, b.id), onSuccess: refresh });
  if (!s) return null;
  const err = toggle.error ?? revive.error;
  if (s.pastAt) {
    const own = b.addedBy?.id === meId;
    const can = !own || s.bumpLeft;
    return (
      <span className="want-wrap">
        <button type="button" className="btn ghost sm upd" disabled={!can || revive.isPending} onClick={() => revive.mutate()} title={own && !s.bumpLeft ? "You've brought this one back once already" : undefined}>
          {own ? "Bring it back" : <>Want to read <b>{s.wanted}</b></>}
        </button>
        {err && <span className="error small">{describeError(err)}</span>}
      </span>
    );
  }
  return (
    <span className="want-wrap">
      <button type="button" className={`btn ghost sm upd${s.mine ? " on" : ""}`} aria-pressed={s.mine} disabled={toggle.isPending} onClick={() => toggle.mutate(!s.mine)}>
        {s.mine ? "✓ " : ""}Want to read <b>{s.wanted}</b>
      </button>
      {err && <span className="error small">{describeError(err)}</span>}
    </span>
  );
}

/** The muted line under a suggestion: who suggested it, and whatever else matters about it right now. */
export function suggestionLine(b: ClubBook, meId?: string, opts: { showRank?: boolean } = {}): React.ReactNode {
  const s = b.suggestion;
  const who = b.addedBy ? `suggested by ${b.addedBy.id === meId ? "you" : b.addedBy.name}` : "suggested";
  const bits: React.ReactNode[] = [who];
  if (opts.showRank && s?.rank) bits.push(s.onDeck ? "on deck" : `${ordinal(s.rank)} in the running`);
  if (s?.pastAt) bits.push(`quiet since ${day(s.pastAt)}`, `${s.wanted} wanted it`);
  if (s?.readIn) bits.push(`the club read this in ${s.readIn}`);
  return (
    <>
      {bits.map((x, i) => <span key={i}>{i > 0 ? " · " : ""}{x}</span>)}
      {s?.movesAt && <span className="notice"> · moves to Past on {day(s.movesAt)} unless someone else wants it</span>}
    </>
  );
}

/** A suggestion as a deck row: cover, title, author, the muted line, "Want to read", and the ⋯ for admins and its suggester. */
export function SuggestionRow({ clubId, b, meId, isAdmin, canStart, onOpen, onStart, showRank }:
  { clubId: string; b: ClubBook; meId?: string; isAdmin: boolean; canStart: boolean; onOpen: () => void; onStart?: () => void; showRank?: boolean }) {
  const refresh = useSuggestionRefresh(clubId);
  const s = b.suggestion;
  const own = b.addedBy?.id === meId;
  const [menu, setMenu] = useState(false);
  const [removing, setRemoving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pin = useMutation({ mutationFn: (p: boolean) => api.setPinned(clubId, b.id, p), onSuccess: refresh });
  const past = useMutation({ mutationFn: () => api.moveToPast(clubId, b.id), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api.removeBook(clubId, b.id), onSuccess: refresh });
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);
  const hasMenu = isAdmin || own || (canStart && onStart);
  const err = pin.error ?? past.error ?? remove.error;
  return (
    <div className="deck-row sugg-row" ref={ref}>
      <span role="button" tabIndex={0} className="deck-hit" onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }} aria-label={`About ${b.book.title}`}><Cover book={b.book} size="sm" /></span>
      <div style={{ minWidth: 0 }}>
        <div className="t"><span role="button" tabIndex={0} className="deck-hit" onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}>{b.book.title}</span>
          {s?.isNew && !s.pastAt && <span className="newmark">new</span>}{s?.pinned && <span className="pinmark">pinned</span>}</div>
        <div className="a">{authorsOf(b.book)}</div>
        {b.note && <div className="note-line">“{b.note}”</div>}
        <div className="d">{suggestionLine(b, meId, { showRank })}</div>
        {removing && (
          <div className="row small" style={{ gap: 6, marginTop: 6 }}>
            <span className="muted">Take it off the shelf for good?</span>
            <button className="btn danger sm" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove</button>
            <button className="btn ghost sm" onClick={() => setRemoving(false)}>Keep</button>
          </div>
        )}
        {err && <p className="error small">{describeError(err)}</p>}
      </div>
      <div className="act">
        <WantButton clubId={clubId} b={b} meId={meId} />
        {hasMenu && <button type="button" className="more" aria-label="More" aria-expanded={menu} onClick={() => setMenu((m) => !m)}><MoreIcon width={18} height={18} /></button>}
        {menu && (
          <div className="menu" role="menu">
            {canStart && onStart && !s?.pastAt && <button role="menuitem" onClick={() => { setMenu(false); onStart(); }}>Start reading</button>}
            {isAdmin && !s?.pastAt && <button role="menuitem" onClick={() => { setMenu(false); pin.mutate(!s?.pinned); }}>{s?.pinned ? "Unpin from on deck" : "Pin to on deck"}</button>}
            {(isAdmin || own) && !s?.pastAt && <button role="menuitem" onClick={() => { setMenu(false); past.mutate(); }}>Move to Past</button>}
            {(isAdmin || own) && <button role="menuitem" className="danger" onClick={() => { setMenu(false); setRemoving(true); }}>Remove</button>}
          </div>
        )}
      </div>
    </div>
  );
}

export const NOTE_MAX = 140;

/** A book's length in club terms: "245 pages · about 3 weeks at 12 a day"; `short` drops the pace, for the inside of a cover. */
export function lengthFor(pages: number | null, pace: number | null, short = false): string | null {
  if (!pages) return null;
  if (!pace) return `${pages} pages`;
  const days = Math.ceil(pages / pace);
  const span = days < 14 ? `about ${days} ${days === 1 ? "day" : "days"}` : `about ${Math.round(days / 7)} weeks`;
  return short ? `${pages} pages · ${span}` : `${pages} pages · ${span} at ${pace} a day`;
}

/** The blurb's first sentence, for a book nobody wrote a note on. */
function firstLine(d: string | null): string | null {
  if (!d) return null;
  const s = d.replace(/\s+/g, " ").trim().split(/(?<=[.!?])\s/)[0] ?? "";
  return s.length > 110 ? `${s.slice(0, 107).trimEnd()}…` : s || null;
}

/** What an on-deck book says about itself: the inside of its cover, and the pane. */
export function factsFor(b: ClubBook, settings: ClubSettings, meId?: string, cover = false): Flyleaf {
  const readers = new Set(b.readBefore?.readers.map((r) => r.id) ?? []);
  return {
    note: b.note ?? null,
    by: b.note && b.addedBy ? (b.addedBy.id === meId ? "you" : b.addedBy.name) : null,
    blurb: b.note ? null : firstLine(b.book.description),
    length: lengthFor(b.book.pages, settings.usualPace, cover),
    card: (b.suggestion?.wantedBy ?? []).map((u) => ({ name: u.id === meId ? "You" : u.name, image: u.image, read: readers.has(u.id) })),
    total: b.suggestion?.wanted ?? 0, // the names are only the first few; this is everyone
  };
}

/** The suggester's one line on why: a small composer with a running count, capped like old Twitter. */
export function NoteEditor({ clubId, b, prompt, onDone }: { clubId: string; b: ClubBook; prompt?: string; onDone: () => void }) {
  const refresh = useSuggestionRefresh(clubId);
  const [text, setText] = useState(b.note ?? "");
  const save = useMutation({ mutationFn: () => api.setNote(clubId, b.id, text), onSuccess: async () => { await refresh(); onDone(); } });
  const left = NOTE_MAX - text.length;
  return (
    <form className="note-edit stack-sm" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      {prompt && <div className="small muted">{prompt}</div>}
      <textarea id={`note-${b.id}`} className="input" rows={2} maxLength={NOTE_MAX} value={text} placeholder="Why this one? One line for the club." onChange={(e) => setText(e.target.value.replace(/\n/g, " "))} autoFocus />
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="btn primary sm" disabled={save.isPending}>Save</button>
        <button type="button" className="btn ghost sm" onClick={onDone}>{b.note ? "Cancel" : "Skip"}</button>
        <span className={`small num${left < 15 ? " warn" : " muted"}`} style={{ marginLeft: "auto" }}>{left}</span>
      </div>
      {save.isError && <p className="error small">{describeError(save.error)}</p>}
    </form>
  );
}
